import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Users, FileText, CheckCircle, Clock, AlertCircle,
  Calendar, Briefcase, ArrowRight, Eye, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_COLORS = {
  interview_scheduled: 'bg-blue-100 text-blue-700',
  interview_completed: 'bg-purple-100 text-purple-700',
  submitted_to_hr: 'bg-yellow-100 text-yellow-700',
  offer_generated: 'bg-orange-100 text-orange-700',
  pending_candidate_signature: 'bg-cyan-100 text-cyan-700',
  pending_sales_approval: 'bg-indigo-100 text-indigo-700',
  pending_director_approval: 'bg-pink-100 text-pink-700',
  completed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700'
};

const STATUS_LABELS = {
  interview_scheduled: 'Interview Scheduled',
  interview_completed: 'Interview Completed',
  submitted_to_hr: 'Submitted to HR',
  offer_generated: 'Offer Generated',
  pending_candidate_signature: 'Awaiting Candidate Signature',
  pending_sales_approval: 'Awaiting Sales Approval',
  pending_director_approval: 'Awaiting Director Approval',
  completed: 'Completed',
  rejected: 'Rejected'
};

// Progress Step Component
function ProgressStep({ step, index, currentIndex, total }) {
  const isCompleted = step.completed;
  const isCurrent = index === currentIndex;
  const isPending = index > currentIndex;

  return (
    <div className="flex items-center">
      <div className="flex flex-col items-center">
        <div 
          className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
            isCompleted 
              ? 'bg-green-500 border-green-500 text-white' 
              : isCurrent 
                ? 'bg-primary border-primary text-white animate-pulse'
                : 'bg-muted border-muted-foreground/30 text-muted-foreground'
          }`}
        >
          {isCompleted ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <span className="text-sm font-medium">{index + 1}</span>
          )}
        </div>
        <span className={`text-xs mt-2 text-center max-w-[80px] ${isCompleted || isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}>
          {step.stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </span>
        {step.date && (
          <span className="text-xs text-muted-foreground">
            {new Date(step.date).toLocaleDateString()}
          </span>
        )}
      </div>
      {index < total - 1 && (
        <div className={`w-12 h-0.5 mx-2 ${isCompleted ? 'bg-green-500' : 'bg-muted'}`} />
      )}
    </div>
  );
}

// Onboarding Card Component
function OnboardingCard({ request, onViewDetails, onGenerateOffer }) {
  const completedSteps = request.progress?.filter(p => p.completed).length || 0;
  const totalSteps = request.progress?.length || 6;
  const percentage = Math.round((completedSteps / totalSteps) * 100);
  const currentIndex = request.progress?.findIndex(p => !p.completed) ?? totalSteps;

  return (
    <Card className="hover:border-primary/30 transition-colors" data-testid={`onboarding-${request.id}`}>
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-lg">{request.candidate?.name}</h3>
              <Badge className={STATUS_COLORS[request.status]}>
                {STATUS_LABELS[request.status]}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {request.position} at {request.client_company}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-primary">{percentage}%</p>
            <p className="text-xs text-muted-foreground">Complete</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <Progress value={percentage} className="h-2" />
        </div>

        {/* Progress Steps */}
        <div className="flex items-start justify-between overflow-x-auto py-4">
          {request.progress?.map((step, index) => (
            <ProgressStep 
              key={step.stage} 
              step={step} 
              index={index} 
              currentIndex={currentIndex}
              total={totalSteps}
            />
          ))}
        </div>

        {/* Details */}
        <div className="grid grid-cols-3 gap-4 py-4 border-t">
          <div>
            <p className="text-xs text-muted-foreground">Salary</p>
            <p className="font-medium">{request.currency} {request.proposed_salary?.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Joining Date</p>
            <p className="font-medium">{request.joining_date}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Submitted By</p>
            <p className="font-medium">{request.submitted_by_name}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-4 border-t">
          <Button variant="outline" size="sm" onClick={() => onViewDetails(request.id)}>
            <Eye className="w-4 h-4 mr-1" />
            View Details
          </Button>
          {request.status === 'submitted_to_hr' && (
            <Button size="sm" onClick={() => onGenerateOffer(request.id)}>
              <FileText className="w-4 h-4 mr-1" />
              Generate Offer Letter
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function OnboardingDashboard() {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      const [requestsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/onboarding/requests`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/onboarding/stats`, { headers: getAuthHeaders() })
      ]);
      setRequests(requestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateOffer = async (requestId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/onboarding/requests/${requestId}/generate-offer`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Offer letter generated successfully');
      fetchData();
    } catch (error) {
      console.error('Error generating offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate offer letter');
    }
  };

  const filteredRequests = filter === 'all' 
    ? requests 
    : requests.filter(r => r.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="onboarding-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Onboarding Pipeline</h1>
          <p className="text-muted-foreground">Track candidate onboarding progress</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{stats?.total_onboarding || 0}</p>
              </div>
              <Users className="w-8 h-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending HR</p>
                <p className="text-2xl font-bold text-yellow-600">{stats?.pending_hr_action || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Sign</p>
                <p className="text-2xl font-bold text-blue-600">{stats?.pending_signatures || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold text-green-600">{stats?.completed || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Interviews</p>
                <p className="text-2xl font-bold text-purple-600">{stats?.interviews_scheduled || 0}</p>
              </div>
              <Calendar className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground mr-2">Filter:</span>
        {['all', 'submitted_to_hr', 'pending_candidate_signature', 'pending_sales_approval', 'pending_director_approval', 'completed'].map((status) => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(status)}
          >
            {status === 'all' ? 'All' : STATUS_LABELS[status]}
          </Button>
        ))}
      </div>

      {/* Onboarding Requests */}
      <div className="space-y-4">
        {filteredRequests.length === 0 ? (
          <Card className="p-12 text-center">
            <Briefcase className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No onboarding requests</h3>
            <p className="text-muted-foreground">Onboarding requests from Sales team will appear here</p>
          </Card>
        ) : (
          filteredRequests.map((request) => (
            <OnboardingCard
              key={request.id}
              request={request}
              onViewDetails={(id) => navigate(`/suite/hr/onboarding/${id}`)}
              onGenerateOffer={handleGenerateOffer}
            />
          ))
        )}
      </div>
    </div>
  );
}
